;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import type { BeforeSendFn, PostHog } from 'posthog-js'
import { BENIGN_EXCEPTIONS } from './benign-exceptions'

/**
 * The only module that imports `posthog-js`, and the only one that may.
 *
 * The SDK is 79 KB gzip — the second-largest thing this site downloads after the framework — and
 * Lighthouse measured 42 KB of that unused at first paint. It was on the critical path because
 * thirteen modules imported it directly, several of them rendered above the fold
 * (`section-view-tracker`, `track-cta`), so deferring only `instrumentation-client.ts` would have
 * moved nothing: the module was pulled into the initial graph either way.
 *
 * With one importer, the SDK gets its own chunk and is fetched once the browser is idle. What makes
 * that safe is the queue: a CTA clicked two seconds in is exactly the event this site exists to
 * record, so calls made before the SDK lands are held rather than dropped, and drained in order.
 *
 * The surface below is deliberately small — it is what the call sites use, nothing more. Adding a
 * method here is the way to reach for a new one; importing `posthog-js` again anywhere else quietly
 * puts all 79 KB back on the critical path, which is why `eslint.config.mjs` forbids it.
 */
type Queued = (client: PostHog) => void

let client: PostHog | null = null
const queue: Queued[] = []

const run = (fn: Queued) => {
  if (client) fn(client)
  else queue.push(fn)
}

export const analytics = {
  capture: (...args: Parameters<PostHog['capture']>) => run((c) => void c.capture(...args)),
  captureException: (...args: Parameters<PostHog['captureException']>) => run((c) => void c.captureException(...args)),
  identify: (...args: Parameters<PostHog['identify']>) => run((c) => c.identify(...args)),
  register: (...args: Parameters<PostHog['register']>) => run((c) => c.register(...args)),
  setPersonProperties: (...args: Parameters<PostHog['setPersonProperties']>) =>
    run((c) => c.setPersonProperties(...args)),
}

/**
 * Loads and initialises the SDK, then drains whatever queued up while it was in flight. Safe to
 * call more than once; the second call is a no-op.
 *
 * Three things can start the load, whichever happens first:
 *
 * - the browser going idle (`requestIdleCallback`, absent in older Safari — hence the timeout),
 * - two seconds, so a visitor on a page that never goes idle is still counted,
 * - the first sign of a human: a pointer down, a touch, a key. This one matters most. The queue
 *   below survives a client-side route change, but not a navigation that leaves the site — and the
 *   CTA that leaves for the app is precisely the event worth having. Starting on the gesture rather
 *   than on the click gives the SDK the width of a click to arrive in.
 */
export function initPostHog(token: string, options: Parameters<PostHog['init']>[1]): void {
  if (client || typeof window === 'undefined') return

  let started = false
  const start = async () => {
    if (started) return
    started = true
    for (const event of GESTURES) window.removeEventListener(event, onGesture)
    const { default: posthog } = await import('posthog-js')
    posthog.init(token, options)
    client = posthog
    // Exposed on `window` exactly as PostHog's own HTML snippet does, for console debugging and for
    // the e2e suite, which asserts on captures. The token is NEXT_PUBLIC_*, so nothing here is
    // secret.
    ;(window as unknown as { posthog: PostHog }).posthog = posthog
    while (queue.length) queue.shift()?.(posthog)
  }
  const onGesture = () => void start()

  for (const event of GESTURES) window.addEventListener(event, onGesture, { once: true, passive: true })

  const idle = window.requestIdleCallback?.bind(window)
  if (idle) idle(() => void start(), { timeout: 2_000 })
  else window.setTimeout(() => void start(), 2_000)
}

const GESTURES = ['pointerdown', 'touchstart', 'keydown'] as const

/**
 * `before_send` for the SDK: drops an `$exception` whose every exception is a benign browser
 * warning (see `BENIGN_EXCEPTIONS` in `./benign-exceptions.ts`), and passes everything else
 * through untouched.
 */
export const dropBenignExceptions: BeforeSendFn = (event) => {
  if (event?.event !== '$exception') return event
  const exceptions = event.properties?.$exception_list as Array<{ value?: unknown }> | undefined
  if (!Array.isArray(exceptions) || exceptions.length === 0) return event
  const benign = exceptions.every(
    (exception) =>
      typeof exception?.value === 'string' &&
      BENIGN_EXCEPTIONS.some((pattern) => pattern.test(exception.value as string)),
  )
  return benign ? null : event
}
